import { colorUtils, idGenerator } from "_src/utils";
import {propertyType, objectType, scanMode} from "_src/constants";
import {
  createProperty,
  reformValue,
  getHexPaletteUnique,
  deepCopy,
  getCadType
} from "_src/reformer/property";
import appState from "_src/app_state";
import {initGeometries, initTextures} from "_src/reformer/common/reformer";

export default {
  initObjectProperty(objectData, type, colorList) {

    const property = createProperty(propertyType.OBJECT);

    property.insertValue("name", reformValue(objectData, "name", () => ""));
    property.insertValue("id", reformValue(objectData, "id", () => idGenerator.getNext()));
    property.insertValue("visible", reformValue(objectData["extra"], "visible", () => true));
    property.insertValue("locked", reformValue(objectData["extra"], "locked", () => false));
    property.insertValue("color", reformValue(objectData["extra"], "color", () => getHexPaletteUnique(colorList)));
    property.insertValue("uuid", reformValue(objectData["extra"], "uuid", () => ""));
    property.insertValue("groupID", reformValue(objectData, "groupid", () => ""));

    property.insertValue("scannertype", reformValue(objectData["extra"], "scannertype", () => ""));
    property.insertValue("deviceserial", reformValue(objectData["extra"], "deviceserial", () => ""));
    property.insertValue("datasize", reformValue(objectData["extra"], "datasize", () => ""));
    property.insertValue("isHD", reformValue(objectData["extra"], "ishd", () => false));
    property.insertValue("texturegain", reformValue(objectData["extra"], "texturegain", () => 0.0));
    property.insertValue("texturecameratype", reformValue(objectData["extra"], "texturecameratype", () => 0));
    property.insertValue("textureillumination", reformValue(objectData["extra"], "textureillumination", () => 0.0));
    property.insertValue("globalapplied", reformValue(objectData["extra"], "globalapplied", () => false));

    property.insertValue("textures", reformValue(objectData, "textures", () => []));

    property.insertValue("transform", reformValue(objectData, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
    property.insertValue("projection", reformValue(objectData, "projection", () => [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]));
    property.insertValue("correction", reformValue(objectData, "correction", () => [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]));

    //Note: we mark geometry source such a way
    property.insertValue("surfelsfile", objectData["surfels"] && objectData["surfels"].length > 0 ? {} : null);
    property.insertValue("geometriesfile", objectData["geometries"] && objectData["geometries"].length > 0 ? {} : null);
    property.insertValue("framesfile", objectData["frames"] && objectData["frames"].length > 0 ? {} : null);

    property.insertValue("cadProperties", objectData["properties"]);
    property.insertValue("cadData", objectData["primitive"]);
    property.insertValue("cadModel", objectData["model"]);
    property.insertValue("cadType", getCadType(objectData));

    property.insertValue("type", type);
    property.insertValue("numPolygons", 0);
    property.insertValue("numVertices", 0);
    property.insertValue("maxError", 0.0);
    property.insertValue("textureWidth", 0);
    property.insertValue("textureHeight", 0);

    const hasTargetScan = objectData["related-object-uuid"] !== undefined;
    const hasRawGeometry = objectData["extra"]["hasrawgeometry"] !== undefined ? objectData["extra"]["hasrawgeometry"] : hasTargetScan;
    const targetRawScan = hasTargetScan ? objectData["related-object-uuid"] : objectData["extra"]["uuid"];
    property.insertValue("hasrawgeometry", hasRawGeometry);
    property.insertValue("targetRawScan", targetRawScan);

    if (type === objectType.FUSION) {
      combineFusionProperty(property, objectData);
    } else {
      combineScanProperty(property, objectData);
    }

    return property;
  },

}

function combineScanProperty(objectProperty, objectData) {

  let numPolygons = 0;
  let numVertices = 0;
  let maxError = 0;
  let numTexturedFrames = 0;
  const isSurfels = appState.getStateValue("scanMode") !== scanMode.TRIANGLES && Boolean(objectData["surfels"]) && objectData["surfels"].length;
  if (isSurfels) {
    const surfelsProperty = createProperty(propertyType.FRAME);
    surfelsProperty.insertValue("name", objectProperty.getValue("name"));
    surfelsProperty.insertValue("id", objectProperty.getValue("id"));
    surfelsProperty.insertValue("color", objectProperty.getValue("color"));
    surfelsProperty.insertValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

    surfelsProperty.insertValue("geometries", initGeometries(objectData["surfels"]));

    let numSurfels = 0;
    for (const geometry of objectData["surfels"]) {
      const level = geometry["levels"][0];
      numSurfels += level["points"];
    }
    surfelsProperty.insertValue("numSurfels", numSurfels);
    objectProperty.getValue("frames").push(surfelsProperty);
    objectProperty.setValue("numSurfels", numSurfels);
  } else {
    for (const frameData of objectData["frames"]) {
      let frameNumPolygons = 0;
      let frameNumVertices = 0;

      const property = createProperty(propertyType.FRAME);

      property.insertValue("name", reformValue(frameData, "name", () => ""));
      property.insertValue("id", reformValue(frameData, "id", () => ""));
      property.insertValue("color", reformValue(frameData["extra"], "color", () => colorUtils.generateHexColor()));
      property.insertValue("error", reformValue(frameData["extra"], "regerror", () => 0.0));
      property.insertValue("keyframe", reformValue(frameData["extra"], "keyframe", () => false));

      property.insertValue("transform", reformValue(frameData, "transform", () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));

      const textures = frameData["textures"] || [];
      const geometries = frameData["geometries"] || [];
      const trackings = frameData["trackings"] || [];

      for (const geometry of geometries) {
        const level = geometry["levels"][0];
        frameNumPolygons += level["triangles"];
        frameNumVertices += level["points"];
      }
      property.insertValue("numPolygons", frameNumPolygons);
      property.insertValue("numVertices", frameNumVertices);

      property.insertValue("textured", textures.length > 0);
      property.insertValue("textures", initTextures(textures));
      property.insertValue("geometries", initGeometries(geometries));
      property.insertValue("trackings", deepCopy(trackings));

      maxError = Math.max(property.getValue("error"), maxError);
      if (textures.length > 0) numTexturedFrames++;
      numVertices += frameNumVertices;
      numPolygons += frameNumPolygons;

      objectProperty.getValue("frames").push(property);
    }
  }

  objectProperty.setValue("numPolygons", numPolygons);
  objectProperty.setValue("numVertices", numVertices);
  objectProperty.setValue("maxError", maxError);
  objectProperty.setValue("numFrames", objectData["frames"].length);
  objectProperty.setValue("numTexturedFrames", numTexturedFrames);
}

function combineFusionProperty(objectProperty, objectData) {
  const property = createProperty(propertyType.FRAME);

  property.insertValue("name", objectProperty.getValue("name"));
  property.insertValue("id", objectProperty.getValue("id"));
  property.insertValue("color", objectProperty.getValue("color"));

  property.insertValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

  const textures = objectProperty.getValue("textures");
  const geometries = objectData["geometries"] || [];

  let numPolygons = 0;
  let numVertices = 0;
  let maxError = 0;
  for (const geometry of geometries) {
    const level = geometry["levels"][0];
    numPolygons += level["triangles"];
    numVertices += level["points"];
  }
  property.insertValue("numPolygons", numPolygons);
  property.insertValue("numVertices", numVertices);

  const numFrames = 1;
  const numTexturedFrames = (objectData["textures"] && objectData["textures"].length > 0) ? 1 : 0;

  objectProperty.setValue("numFrames", numFrames);
  objectProperty.setValue("numTexturedFrames", numTexturedFrames);
  objectProperty.setValue("numPolygons", numPolygons);
  objectProperty.setValue("numVertices", numVertices);
  objectProperty.setValue("maxError", maxError);

  property.insertValue("textures", initTextures(textures));
  property.insertValue("geometries", initGeometries(geometries));
  property.insertValue("textured", textures.length > 0);

  objectProperty.getValue("frames").push(property);
}
